import React, { useState, useEffect } from "react";
import {
  Typography,
  Input,
  Radio,
  Button,
  Form,
  Card,
  message,
  Select,
  Space,
} from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import { firebaseAPI } from "../firebaseConfig";
import axiosInstance from "../utils/axiosInstance";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Title } = Typography;
const { Option } = Select;

const NotificationsPage = () => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [recipientRole, setRecipientRole] = useState("");
  const [participantStatus, setParticipantStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [programRounds, setProgramRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState("");
  const [recipients, setRecipients] = useState([]); // emails or IDs
  const [flowType, setFlowType] = useState("direct"); // or "group"

  useEffect(() => {
    fetchProgramRounds();
  }, []);

  const fetchProgramRounds = async () => {
    try {
      const programName = window.location.pathname.split("/")[1];
      const response = await axiosInstance.get(
        `${firebaseAPI}/program/${programName}/rounds`
      );
      const { programRounds } = response.data;
      setProgramRounds(programRounds);
    } catch (error) {
      console.error("Error fetching program rounds:", error);
    }
  };

  const handleExportParticipants = async () => {
    setExportLoading(true);
    const programName = window.location.pathname.split("/")[1];
    const token = Cookies.get("token") || "";

    if (!selectedRound || !participantStatus) {
      message.error("Please select both round and participant status.");
      return;
    }

    try {
      // Updated API endpoint for exporting participants from new backend
      const response = await axiosInstance.post(
        `${firebaseAPI}/export-participantsForNotificationPage`, // This now points to your new backend route
        {
          programName,
          roundName: selectedRound,
          participantStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob", // Expecting a CSV file
        }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `participants_${programName}_${selectedRound}_${participantStatus}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportLoading(false);
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Failed to export participants.");
    }
  };

  const handleSubmit = async () => {
    const programName = window.location.pathname.split("/")[1];
    setLoading(true);
    try {
      const token = Cookies.get("token") || "";
      console.log(content);

      // 1️⃣ If user-entered recipients exist, send direct emails using the batch recipients endpoint
      if (recipients && recipients.length > 0) {
        const response = await axiosInstance.post(
          `${firebaseAPI}/send-email`,
          {
            recipients, // array of emails
            subject,
            body: content,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          message.success("All emails sent successfully!");
        } else if (response.status === 207) {
          message.warning(
            response.data.message || "Some emails failed to send."
          );
          if (response.data.failures?.length) {
            console.warn("Failed recipients:", response.data.failures);
          }
        } else {
          message.error("Email sending failed. Please try again.");
        }
      }

      // 2️⃣ Otherwise, use your role-based notification API
      else {
        const requestBody = {
          subject,
          content,
          recipientRole,
          programName,
          roundName: selectedRound,
          ...(recipientRole === "participant" && { participantStatus }),
        };

        const response = await axiosInstance.post(
          `${firebaseAPI}/send-notifications`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          message.success("Notification sent successfully.");
        } else {
          message.error("Failed to send notification. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
      }}
    >
      <Card
        style={{
          width: "70vw",
          padding: 20,
          borderRadius: 10,
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={2}>Send Notification</Title>
          {recipientRole === "participant" && flowType === "group" && (
            <Button
              loading={exportLoading}
              type="default"
              onClick={handleExportParticipants}
            >
              Export Participants
            </Button>
          )}
        </div>

        <p>
          <b>Note:</b> The user can only receive one email per round.
        </p>

        {/* Flow Toggle */}
        <Radio.Group
          value={flowType}
          onChange={(e) => setFlowType(e.target.value)}
          style={{ marginBottom: 20 }}
        >
          <Radio value="direct">Send to Specific Emails</Radio>
          <Radio value="group">Send to User Group</Radio>
        </Radio.Group>

        <Form
          layout="vertical"
          onFinish={(values) =>
            handleSubmit({
              ...values,
              recipients,
            })
          }
        >
          {/* Subject */}
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: "Please enter the subject" }]}
          >
            <Input
              placeholder="Enter the subject"
              size="large"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Form.Item>

          {/* Content */}
          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: "Please enter the content" }]}
          >
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="Write your email content here..."
              style={{ height: 250, marginBottom: 20 }}
            />
          </Form.Item>

          {/* Direct Email Flow */}
          {flowType === "direct" && (
            <Form.Item
              label="Recipients"
              name="recipients"
              rules={[
                {
                  required: true,
                  message: "Please enter at least one recipient email",
                },
                {
                  validator: (_, value = []) => {
                    const invalid = value.find(
                      (email) =>
                        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
                    );
                    return invalid
                      ? Promise.reject(new Error(`Invalid email: ${invalid}`))
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Type or paste email addresses and press Enter"
                value={recipients}
                onChange={setRecipients}
                tokenSeparators={[",", ";", " "]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}

          {/* Group Notification Flow */}
          {flowType === "group" && (
            <>
              <Form.Item
                label="Recipient Role"
                name="recipientRole"
                rules={[
                  {
                    required: true,
                    message: "Please select the recipient role",
                  },
                ]}
              >
                <Select
                  placeholder="Select the recipient role"
                  value={recipientRole}
                  onChange={setRecipientRole}
                >
                  <Option value="participant">Participant</Option>
                  <Option value="judge">Judge</Option>
                  <Option value="admin">Admin</Option>
                </Select>
              </Form.Item>

              {/* Only show filters if participant */}
              {recipientRole === "participant" && (
                <>
                  <Form.Item
                    label="Participant Status"
                    name="participantStatus"
                    rules={[
                      {
                        required: true,
                        message: "Please select participant status",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select the participant status"
                      value={participantStatus}
                      onChange={setParticipantStatus}
                    >
                      <Option value="qualified">
                        <Space>
                          <span style={{ color: "#52c41a" }}>●</span>Qualified
                        </Space>
                      </Option>
                      <Option value="disqualified">
                        <Space>
                          <span style={{ color: "#ff4d4f" }}>●</span>
                          Disqualified
                        </Space>
                      </Option>
                      <Option value="pending">
                        <Space>
                          <span style={{ color: "#faad14" }}>●</span>Under
                          Review
                        </Space>
                      </Option>
                      <Option value="awaiting_submission">
                        <Space>
                          <span style={{ color: "#d9d9d9" }}>●</span>Awaiting
                          Submission
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Round"
                    name="round"
                    rules={[
                      { required: true, message: "Please select the round" },
                    ]}
                  >
                    <Select
                      placeholder="Select the round"
                      value={selectedRound}
                      onChange={setSelectedRound}
                    >
                      {programRounds.map((r) => (
                        <Option key={r} value={r}>
                          {r}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}
            </>
          )}

          {/* Submit */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{ borderRadius: 5 }}
            >
              Send Notification
            </Button>
          </Form.Item>
        </Form>

        {/* Preview + Copy */}
        <div style={{ marginTop: 20 }}>
          <Title level={4}>Preview</Title>
          <div
            id="email-preview"
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 5,
              padding: 15,
              background: "#fafafa",
              whiteSpace: "pre-wrap",
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
          <Button
            style={{ marginTop: 10 }}
            type="dashed"
            onClick={() => {
              const el = document.getElementById("email-preview");
              if (!el) return;
              const range = document.createRange();
              range.selectNode(el);
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
              document.execCommand("copy")
                ? message.success("Copied!")
                : message.error("Copy failed");
              sel.removeAllRanges();
            }}
          >
            📋 Copy Email Body
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default NotificationsPage;
